import {memo, useCallback, useState} from 'react';

import styled from 'styled-components';

import TrashIcon from '~/assets/images/icons/pie/functionalityTrashSmall.svg?react';
import {getLocalizationService} from '~/shared/services/localisationService';
import {
  creditCardTypesEnum,
  paymentCardsIconComponents,
} from '~/shared/utils/payments/paymentMethodsUtils';
import {smallBold, smallRegular} from '~/shared/theme/typography';
import PieButton from '~/shared/theme/pie/PieButton';
import {flipOnLTR, media} from '~/shared/theme/utils';
import ClickOutsideDiv from '~/shared/components/ClickOutsideDiv';

import FunctionalitySmallVertical from './functionalityVerticalSmall.svg?react';

const Root = styled.div`
  position: relative;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${media.minLargeMobile`
    padding: 16px 0;
  `}
`;

const CardInfoWrapper = styled.div`
  display: flex;
  align-items: unset;
  gap: 16px;
  min-height: 44px;
`;

const CardTitle = styled.p`
  ${smallBold}
  color: ${({theme}) => theme.colors.charcoal80};
  margin-bottom: 4px;
`;

const CardExpiryDate = styled.p<{isFixedLTR: boolean}>`
  ${smallRegular}
  color: ${({theme}) => theme.colors.charcoal80};
  ${({isFixedLTR, theme}) => isFixedLTR && `
    direction: ltr;
    text-align: ${theme.isLTR ? 'right' : 'left'};
  `}
`;

const CardExpiredMessage = styled.p`
  ${smallRegular}
  color: ${({theme}) => theme.colors.contentError};
`;

const StyledTrashIcon = styled(TrashIcon)`
  ${flipOnLTR`
    margin-left: 4px;
  `}
`;

const DropdownContainer = styled(ClickOutsideDiv)`
  position: absolute;
  top: 64px;
  padding: 8px 0;
  background-color: ${({theme}) => theme.colors.surface};
  width: 160px;
  z-index: 1;
  box-shadow: ${({theme}) => theme.shadows.cardElevation};
  border-radius: 12px;
  ${flipOnLTR`
    left: 16px;
  `}
  ${media.minLargeMobile`
    ${flipOnLTR`
      left: 0;
    `}
  `}
`;

const DropdownOption = styled.div`
  display: block;
  margin: 0;
  padding: 12px 16px;
  cursor: pointer;

  :hover {
    background-color: ${({theme}) => theme.colors.actionWhiteHover};
  }

  :active {
    background-color: ${({theme}) => theme.colors.actionWhiteActive};
  }
`;

const DropdownOptionContent = styled.p`
  color: ${({theme}) => theme.colors.charcoal80};
  ${smallRegular}
  text-align: start;
`;

const getCardTypeImage = (type: keyof typeof creditCardTypesEnum, brand?: keyof typeof creditCardTypesEnum) => {
  try {
    if (type === 'paypal') {
      return paymentCardsIconComponents[creditCardTypesEnum.paypal];
    }
    return brand && brand in creditCardTypesEnum ? paymentCardsIconComponents[creditCardTypesEnum[brand]] : paymentCardsIconComponents[creditCardTypesEnum.newcreditcard];
  } catch (error) {
    return paymentCardsIconComponents[creditCardTypesEnum.newcreditcard];
  }
};

type PaymentProps = {
  onRemovePaymentClick: (args: {id: number; cardName: string; type: string}) => void;
  id: number;
  isB2B: boolean;
  dataTest?: string;
  type: keyof typeof creditCardTypesEnum;
  brand?: keyof typeof creditCardTypesEnum;
  suffix?: string;
  expiryDate?: string;
  email?: string; // used with PayPal only
  isExpired?: boolean;
};

const Payment = ({id, isB2B, dataTest, type, brand, suffix, expiryDate, email, isExpired, onRemovePaymentClick}: PaymentProps) => {
  const {t} = getLocalizationService();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const CardImage = getCardTypeImage(type, brand);
  const isPaypal = type === 'paypal';
  
  const removePayment = useCallback(() => {
    onRemovePaymentClick({id, cardName: isPaypal ? t('paypal') : t('card_xxx', {suffix}), type});
    setIsDropdownOpen(false);
  }, [onRemovePaymentClick, id, isPaypal, t, suffix, type]);

  const paymentButtonClickHandler = useCallback(() => {
    if (isB2B) {
      setIsDropdownOpen(state => !state);
      return;
    }
    removePayment();
  }, [isB2B, removePayment]);

  return (
    <Root data-test-id={dataTest}>
      <CardInfoWrapper>
        <CardImage className="avoid-fill-stroke" />
        <div>
          <CardTitle data-test-id="cardName">{isPaypal ? t('paypal') : suffix}</CardTitle>
          {!isExpired && (expiryDate || email) && <CardExpiryDate data-test-id="expDate" isFixedLTR={!!email}>{expiryDate || email}</CardExpiryDate>}
          {isExpired && <CardExpiredMessage data-test-id="expDate">{t('credit_card_expired')}</CardExpiredMessage>}
        </div>
      </CardInfoWrapper>
      <PieButton
        iconButton={isB2B}
        data-test-id="removePaymentBtn"
        onClick={paymentButtonClickHandler}
        variant="outlineSmall"
      >
        {isB2B ? <FunctionalitySmallVertical /> : <StyledTrashIcon />}
        {!isB2B && t('remove')}
      </PieButton>
      {isDropdownOpen && (
        <DropdownContainer onClickOutside={() => setIsDropdownOpen(false)}>
          <DropdownOption onClick={removePayment}>
            <DropdownOptionContent>{t('payment_method_remove_title')}</DropdownOptionContent>
          </DropdownOption>
          {type !== 'paypal' && (
            <DropdownOption as="a" href="/Moneycard/MoneycardOtlRuleManagement">
              <DropdownOptionContent>{t('set_credit_limit')}</DropdownOptionContent>
            </DropdownOption>
          )}
        </DropdownContainer>
      )}
    </Root>
  );
};

export default memo(Payment);
